/* Plan changes, in the console (2026-10-28), from the classic
   SubscriptionManager — only the parts that work for a school:

   - Upgrade: the server's prorated charge on the saved card, or Paystack's
     hosted checkout when there is none (POST /api/subscription/upgrade-proration).
   - Cancel at the end of the paid period, and resume (…/cancel, …/resume).
   - Enterprise: add a branch (…/add-branch), billed onto the group's renewal.

   A downgrade, a new billing cycle and an add-on are a request to Trasse. The
   classic dashboard wrote them straight onto the school record, which the
   rules refuse for every school admin (only PEAR may write subscriptionPlan,
   billingPeriod or pendingPlanChange), so those buttons failed without a word;
   and the renewal job never reads a changed cycle at all. */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  addBranch, BranchQuote, cancelSubscription, previewAddBranch, previewUpgradeProration, ProrationPreview,
  switchBranch, upgradeProration,
} from '../services/paymentsApi';
import { SUPPORT_WHATSAPP_HREF } from '../utils/companyContact';
import { ConsoleModal, fieldInput, ModalButtons, ModalField } from './Modal';
import { fullDate, n0, useToast } from './kit';

const money = (n: number | undefined, currency?: string) =>
  `${!currency || currency === 'KES' ? 'Ksh' : currency} ${n0(Math.round(n || 0))}`;

export const waHref = (text: string) => `${SUPPORT_WHATSAPP_HREF}?text=${encodeURIComponent(text)}`;

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ margin: 0, fontSize: 16, lineHeight: '24px' }}>{children}</p>
);
const Quiet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ margin: 0, fontSize: 14, lineHeight: '20px', color: 'var(--muted)' }}>{children}</p>
);
const Big: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontFamily: "'Barlow Semi Condensed', Arial", fontWeight: 700, fontSize: 40, lineHeight: '44px' }}>{children}</div>
);

/** Premium → Enterprise (or up from a retired plan): the prorated charge now. */
export const UpgradeModal: React.FC<{ orgCode: string; plan: 'premium' | 'enterprise'; planName: string; onClose: () => void }> = ({ orgCode, plan, planName, onClose }) => {
  const { toast } = useToast();
  const [preview, setPreview] = useState<ProrationPreview | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    previewUpgradeProration(orgCode, plan).then((p) => { if (live) setPreview(p); });
    return () => { live = false; };
  }, [orgCode, plan]);

  const pay = () => {
    if (busy || !preview?.ok) return;
    setBusy(true);
    upgradeProration(orgCode, plan)
      .then((r) => {
        if (r.ok) { toast({ kind: 'success', text: `You’re on ${planName}. Zana starts learning your fleet — insights begin shortly.` }); onClose(); return; }
        if (r.fallback === 'hosted' && r.authorization_url) { window.location.href = r.authorization_url; return; }
        toast({ kind: 'error', text: r.message || 'The payment didn’t go through. Try again, or pay another way.' });
      })
      .finally(() => setBusy(false));
  };

  const amount = money(preview?.amountKES, preview?.currency);
  return (
    <ConsoleModal title={`Move to ${planName}`} onClose={onClose} width={520}
      footer={<ModalButtons onCancel={onClose} primary={preview?.ok ? `Pay ${amount}` : 'Pay'} onPrimary={pay} busy={busy || preview === null} disabled={!preview?.ok} />}>
      {preview === null && <Quiet>Working out the amount…</Quiet>}
      {preview && !preview.ok && (
        <>
          <Note>{preview.message || 'We couldn’t work out the amount.'}</Note>
          <a className="btn b-line" style={{ alignSelf: 'flex-start' }} target="_blank" rel="noreferrer"
            href={waHref(`We would like to move ${orgCode} to ${planName}.`)}>Ask Trasse on WhatsApp</a>
        </>
      )}
      {preview?.ok && (
        <>
          <div>
            <Big>{amount}</Big>
            <Quiet>today{preview.daysRemaining != null ? `, for the ${preview.daysRemaining === 1 ? 'day' : `${preview.daysRemaining} days`} left in this billing period` : ''}</Quiet>
          </div>
          <Note>
            {planName} starts as soon as the payment goes through. From your next renewal you pay the full {planName} rate
            {preview.newFullTermKES ? ` — ${money(preview.newFullTermKES, preview.currency)}` : ''}.
          </Note>
          <Quiet>{preview.hasSavedCard === false ? 'You pay on Paystack: M-PESA or a card.' : 'We charge the card on file. If it is declined, you pay on Paystack instead.'}</Quiet>
        </>
      )}
    </ConsoleModal>
  );
};

/** A change a school can't make itself: Trasse makes it, from a WhatsApp message. */
export const AskTrasseModal: React.FC<{ title: string; body: React.ReactNode; message: string; onClose: () => void }> = ({ title, body, message, onClose }) => (
  <ConsoleModal title={title} onClose={onClose} width={520}
    footer={(
      <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="btn b-line" onClick={onClose}>Cancel</button>
        <a className="btn b-teal" href={waHref(message)} target="_blank" rel="noreferrer" onClick={onClose}>Send on WhatsApp</a>
      </div>
    )}>
    {body}
  </ConsoleModal>
);

const REASONS = [
  'Too expensive',
  'Missing features we need',
  'Switching to another provider',
  'The school is closing',
  'Only needed it for a while',
  'Something else',
] as const;

/** Cancel at the end of the paid period — the policy on /terms. */
export const CancelModal: React.FC<{ orgCode: string; accessEnds: Date | null; onClose: () => void }> = ({ orgCode, accessEnds, onClose }) => {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = () => {
    setTried(true);
    if (!reason || busy) return;
    setBusy(true);
    cancelSubscription(orgCode, note.trim() ? `${reason} — ${note.trim()}` : reason)
      .then((r) => {
        if (!r.ok) { toast({ kind: 'error', text: r.message || 'Couldn’t cancel. Try again, or ask Trasse.' }); return; }
        toast({ kind: 'success', text: accessEnds ? `Cancelled. Everything keeps working until ${fullDate(accessEnds)}, and you won’t be billed again.` : 'Cancelled. You won’t be billed again.' });
        onClose();
      })
      .finally(() => setBusy(false));
  };

  return (
    <ConsoleModal title="Cancel the subscription?" onClose={onClose} width={540}
      footer={<ModalButtons cancel="Keep it" onCancel={onClose} primary="Cancel subscription" danger busy={busy} onPrimary={submit} />}>
      <Note>
        {accessEnds ? <>Everything keeps working until <b>{fullDate(accessEnds)}</b>. </> : null}
        You won’t be billed again, and nothing already paid is refunded. You can change your mind until then.
      </Note>
      <div role="radiogroup" aria-label="Why are you cancelling?" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>Why are you cancelling?</span>
        {REASONS.map((r) => (
          <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 46, padding: '0 14px', borderRadius: 14, background: 'var(--s2)', cursor: 'pointer', boxShadow: reason === r ? 'inset 0 0 0 2px var(--ink)' : 'none' }}>
            <input type="radio" name="cancel-reason" checked={reason === r} onChange={() => setReason(r)} style={{ accentColor: 'var(--ink)', width: 18, height: 18, margin: 0 }} />
            <span style={{ fontSize: 15 }}>{r}</span>
          </label>
        ))}
        {tried && !reason && <span role="alert" style={{ fontSize: 14, fontWeight: 600, color: 'var(--bad)' }}>Pick one — it helps us improve.</span>}
      </div>
      <ModalField label="Anything else? (optional)">
        {(id, ring) => <input id={id} {...ring} style={fieldInput} value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />}
      </ModalField>
    </ConsoleModal>
  );
};

/** Enterprise: a whole new branch, billed on its own size onto the group's renewal. */
export const BranchModal: React.FC<{ orgCode: string; unit: 'child' | 'vehicle'; onClose: () => void }> = ({ orgCode, unit, onClose }) => {
  const { toast } = useToast();
  const { refreshAdminStatus } = useAuth();
  const [name, setName] = useState('');
  const [size, setSize] = useState('');
  const [quote, setQuote] = useState<BranchQuote | null>(null);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const n = parseInt(size, 10) || 0;

  // Priced as the size settles, not on every key.
  useEffect(() => {
    if (n <= 0) { setQuote(null); return; }
    let live = true;
    const t = window.setTimeout(() => { previewAddBranch(orgCode, n).then((q) => { if (live) setQuote(q); }); }, 400);
    return () => { live = false; window.clearTimeout(t); };
  }, [orgCode, n]);

  const submit = () => {
    setTried(true);
    if (busy || !name.trim() || n <= 0) return;
    setBusy(true);
    addBranch(orgCode, { name: name.trim(), size: n })
      .then(async (r) => {
        if (r.ok && r.orgCode) {
          const sw = await switchBranch(r.orgCode);
          if (sw.ok) { await refreshAdminStatus(); toast({ kind: 'success', text: `${name.trim()} added. You’re in it now — switch back from your profile menu.` }); }
          else toast({ kind: 'success', text: `${name.trim()} added. Open it from Branches on this page.` });
          onClose();
          return;
        }
        if (r.fallback === 'hosted' && r.authorization_url) { window.location.href = r.authorization_url; return; }
        toast({ kind: 'error', text: r.message || 'Couldn’t add the branch. Try again.' });
      })
      .finally(() => setBusy(false));
  };

  const word = unit === 'child' ? 'children' : 'vehicles';
  return (
    <ConsoleModal title="Add a branch" onClose={onClose} onSubmit={submit} enterHint="add"
      footer={<ModalButtons onCancel={onClose} primary={quote?.ok && !quote.manual && quote.amountKES ? `Add and pay ${money(quote.amountKES)}` : 'Add branch'} onPrimary={submit} busy={busy} />}>
      <Quiet>A branch is a school of its own — its own buses, drivers, parents and logins — on your Enterprise plan, renewing with the rest of the group.</Quiet>
      <ModalField label="Branch name" error={tried && !name.trim() ? 'Give the branch a name.' : null}>
        {(id, ring) => <input id={id} {...ring} style={fieldInput} value={name} placeholder="Karen campus" onChange={(e) => setName(e.target.value)} />}
      </ModalField>
      <ModalField label={`How many ${word}`} width={220} error={tried && n <= 0 ? `How many ${word} it has.` : null}>
        {(id, ring) => <input id={id} {...ring} style={fieldInput} inputMode="numeric" value={size} placeholder="120" onChange={(e) => setSize(e.target.value.replace(/\D/g, '').slice(0, 5))} />}
      </ModalField>
      {quote && (quote.ok
        ? quote.manual
          ? <Note>Billed by Trasse, off the app — nothing to pay here.</Note>
          : <Note><b>{money(quote.amountKES)}</b> today to line it up with your group’s renewal{quote.renewsOn ? ` on ${fullDate(new Date(quote.renewsOn))}` : ''}, then <b>{money(quote.recurringKES)}</b> each time you renew.</Note>
        : <Quiet>{quote.message || 'Couldn’t price the branch yet.'}</Quiet>)}
    </ConsoleModal>
  );
};
